import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // Use borderBoxSize from the entry to avoid a forced reflow:
          // blockSize is the cached height from the observer, no layout read needed.
          const blockSize = entry.borderBoxSize?.[0]?.blockSize;
          if (blockSize !== undefined) {
            this.#applyClamp(blockSize);
          } else {
            this.#calculateTruncation();
          }
        }
      });
      // Defer observe() so the initial ResizeObserver callback fires AFTER
      // the static rAF batch has already applied the first clamp — avoids
      // a redundant recalculation in the same frame as the upgrade batch.
      setTimeout(() => { this.resizeObserver?.observe(this); }, 0);
    } else {
      /** @type {Window} */
      (window).addEventListener('resize', this.#handleResize.bind(this));
    }

    // Add to the global batch. All product-title elements that connect in the
    // same synchronous task (e.g. 70 cards upgrading at once) share one rAF:
    // phase-1 reads ALL clientHeight values, phase-2 writes ALL clamp styles.
    // This eliminates the write→read forced reflow between consecutive elements.
    ProductTitle.#scheduleBatch(this);
  }

  // ── Static batch machinery ────────────────────────────────────────────────

  /** @type {ProductTitle[]} */
  static #batch = [];

  /** @type {number} rAF handle; 0 means no pending batch */
  static #rafId = 0;

  /** @param {ProductTitle} el */
  static #scheduleBatch(el) {
    ProductTitle.#batch.push(el);
    if (ProductTitle.#rafId) return;
    ProductTitle.#rafId = requestAnimationFrame(() => {
      ProductTitle.#rafId = 0;
      const els = ProductTitle.#batch.splice(0);

      // Phase 1 — read all measurements (zero writes → zero forced reflows)
      const tasks = els.map((el) => {
        const textEl = el.refs?.text || el.querySelector('.title-text') || el;
        if (!textEl.textContent) return null;
        const h = el.clientHeight;
        if (h === 0) return null;
        const cs = getComputedStyle(el);
        const lineHeight = parseFloat(cs.lineHeight);
        if (!lineHeight) return null;
        const maxLines = Math.max(
          1,
          Math.floor((h - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom)) / lineHeight)
        );
        return { textEl, maxLines };
      });

      // Phase 2 — write all styles (no reads → no forced reflows)
      for (const t of tasks) {
        if (!t) continue;
        ProductTitle.#writeClamp(t.textEl, t.maxLines);
      }
    });
  }

  // ── Instance helpers ──────────────────────────────────────────────────────

  /**
   * @param {number} containerHeight
   */
  #applyClamp(containerHeight) {
    const textEl = this.refs?.text || this.querySelector('.title-text') || this;
    if (!textEl.textContent || containerHeight === 0) return;
    const cs = getComputedStyle(this);
    const lineHeight = parseFloat(cs.lineHeight);
    if (!lineHeight) return;
    const maxLines = Math.max(
      1,
      Math.floor((containerHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom)) / lineHeight)
    );
    ProductTitle.#writeClamp(/** @type {HTMLElement} */ (textEl), maxLines);
  }

  /**
   * @param {HTMLElement} textEl
   * @param {number} maxLines
   */
  static #writeClamp(textEl, maxLines) {
    const s = textEl.style;
    s.display = '-webkit-box';
    s.webkitBoxOrient = 'vertical';
    s.overflow = 'hidden';
    s.textOverflow = 'ellipsis';
    s.webkitLineClamp = String(maxLines);
  }

  #calculateTruncation() {
    this.#applyClamp(this.clientHeight);
  }

  /**
   * Handle window resize events
   */
  #handleResize() {
    this.#calculateTruncation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
